import {
  AnchorButton,
  Button,
  Colors,
  ControlGroup,
  Dialog,
  FormGroup,
  H4,
  HTMLSelect,
  InputGroup,
  Intent,
  Tag
} from '@blueprintjs/core'
import { IconNames } from '@blueprintjs/icons'
import Immutable from 'immutable'
import React from 'react'
import { connect } from 'react-redux'
import { RouteComponentProps } from 'react-router'
import Client from '../../../client'
import { LoadableSelect } from '../../../components/Inputs'
import Widget from '../../../components/Widget'
import config from '../../../config'
import { CurrentState } from '../../../current/types'
import { AppState } from '../../../store'
import { UprightStyle } from '../../../types'
import { showSuccess } from '../../../Toast'

type Props = RouteComponentProps<{ id: string }> & {
  current: CurrentState
  // true if we should display a limited page for editing the current user's account, http://localhost:3000/edit_account
  editAccount?: boolean
}

const styles: UprightStyle = {
  supplierValidation: { border: `1px solid ${Colors.RED3}` },
  userSelectNone: { userSelect: 'none' },
  flex: { display: 'flex' }
}

class NewUser extends React.Component<Props, { atom: any; showPassword: boolean; showResetMfaDialog: boolean }> {
  state = {
    atom: Immutable.fromJS({
      mode: 'new',
      loading: false,
      selectedRole: null,
      roleInputDisabled: false,
      error: {
        message: '',
        errors: {}
      },
      roles: {
        id: '',
        name: ''
      },
      user: {
        username: '',
        name: '',
        email: '',
        password: '',
        active: true,
        role: 'user',
        shopgoodwillUsername: '',
        shopgoodwillPassword: '',
        supplierId: null,
        localStoreAttributes: null,
        roleId: null,
        lockedUntil: null,
        lockoutReason: null,
        failedLoginAttempts: 0
      },
      id: ''
    }),
    showPassword: false,
    showResetMfaDialog: false
  }

  fetchData = async () => {
    let { id } = this.props.match.params

    if (!id && this.props.editAccount && this.props.current.user.id) {
      id = `${this.props.current.user.id}`
    }

    if (id) {
      this.setState(state => ({
        atom: state.atom.set('mode', 'edit').set('loading', true).set('error', null).set('id', id)
      }))
    }
    if (this.props.current.user.permissions.includes('settings.team')) {
      await this.fetchRoles()
    }
    if (id) {
      await this.fetchUser(id)
    }

    this.setState(state => ({
      atom: state.atom.set('loading', false)
    }))
  }

  componentDidMount() {
    this.fetchData()
  }

  fetchRoles = async () => {
    try {
      const res = await Client.v5GetRoles(false)

      // Default to user role when initially fetching role. This ensures the roles dropdown has a valid selection when creating a new user
      const { roles } = res.data
      if (roles.length === 0) {
        // Roles should always be returned. Default roles are available to all organizations.
        // This should not happen so something has gone wrong with the api response. Raise an error.
        throw new Error('No roles found.')
      }

      this.setState(state => {
        // "User" role will be changed to "Poster" for 2021 RBAC rollout.
        let selectedRole = roles.find(role => role.name === 'Poster')

        // If default cannot be found, fallback to first available role. Default roles will always exist
        if (!selectedRole) {
          ;[selectedRole] = roles
        }

        const atom = state.atom.set('roles', roles).set('selectedRole', selectedRole)
        return {
          atom
        }
      })
    } catch (error) {
      this.setState(state => ({
        atom: state.atom
          .set(
            'error',
            Immutable.fromJS({
              message:
                "Something went wrong while fetching your organization's roles, please refresh the page and try again."
            })
          )
          .set('loading', false)
      }))
    }
  }

  fetchUser = async userId => {
    try {
      const res = await Client.getUser(userId)

      this.setState(state => {
        const roles = state.atom.get('roles')
        let selectedRole = roles.find(role => role.id === res.data.user.roleId)

        // If default cannot be found, fallback to first available role. Default roles will always exists
        if (!selectedRole) {
          ;[selectedRole] = roles
        }

        const userIsCurrentUser = this.props.current.user.id === res.data?.user?.id
        const atom = state.atom
          .set('user', Immutable.fromJS(res.data.user))
          .set('selectedRole', selectedRole)
          .set('roleInputDisabled', userIsCurrentUser)

        return {
          atom
        }
      })
    } catch (error) {
      this.setState(state => ({
        atom: state.atom.set('error', Immutable.fromJS(error.data.errors)).set('loading', false)
      }))
    }
  }

  submit = async e => {
    e.preventDefault()
    const mode = this.state.atom.get('mode')
    const user = this.state.atom.get('user').toJS()
    this.setState(state => ({ atom: state.atom.set('loading', true).set('error', null) }))
    try {
      const action = mode === 'edit' ? Client.updateUser(this.state.atom.get('id'), user) : Client.createUser(user)
      const res = await action
      this.setState(
        state => ({
          atom: state.atom.set('data', Immutable.fromJS(res.data)).set('loading', false)
        }),
        () => {
          if (this.props.editAccount) {
            showSuccess('Successfully updated your account')
          } else {
            this.props.history.push('/admin/users')
          }
        }
      )
    } catch (error) {
      this.setState(state => ({
        atom: state.atom.set('loading', false).set('error', Immutable.fromJS(error.response.data))
      }))
    }
    if (this.props.current.user.id === user.id && !this.props.editAccount) window.location.reload()
  }

  handleChange = (k, v) => {
    this.setState(state => ({
      atom: state.atom.setIn(['user', k], v)
    }))
  }

  onRoleChangeEvent = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const roleId = e.target.value
    this.setState(state => {
      let atom = state.atom.setIn(['user', 'roleId'], roleId)

      const selectedRole = atom.get('roles').find(role => role.id === +roleId)
      atom = atom.set('selectedRole', selectedRole)

      if (!selectedRole.userPermissions.includes('products.post')) {
        // This is safe to do even if current doesn't have a sgw account (it's the default state)
        atom = atom.setIn(['user', 'shopgoodwillUsername'], '')
        atom = atom.setIn(['user', 'shopgoodwillPassword'], '')
      }
      return {
        atom
      }
    })
  }

  onAssignSupplier = supplierId => {
    this.setState(state => {
      let atom = state.atom.setIn(['user', 'supplierId'], supplierId)
      if (!supplierId) {
        atom = atom.setIn(['user', 'localStoreAttributes'], null)
      }
      return {
        atom
      }
    })
  }

  handleLocalStoreChange = (field, val) => {
    if (this.state.atom.getIn(['user', 'localStoreAttributes'])) {
      this.setState(state => ({
        atom: state.atom.setIn(['user', 'localStoreAttributes', field], val)
      }))
    } else {
      this.setState(state => ({
        atom: state.atom.setIn(['user', 'localStoreAttributes'], Immutable.fromJS({ [field]: val }))
      }))
    }
  }

  handleUnlock = async () => {
    const userId = this.state.atom.getIn(['user', 'id'])
    try {
      await Client.unlockUserAccount(userId)
      await this.fetchUser(userId)
      showSuccess('Account successfully unlocked')
    } catch (error) {
      this.setState(state => ({
        atom: state.atom.set('loading', false).set('error', Immutable.fromJS(error.response?.data || {}))
      }))
    }
  }

  handleResetMfaClick = () => {
    this.setState({ showResetMfaDialog: true })
  }

  handleResetMfaConfirm = async () => {
    const userId = this.state.atom.getIn(['user', 'id'])
    this.setState({ showResetMfaDialog: false })
    try {
      await Client.resetUserMfa(userId)
      await this.fetchUser(userId)
      showSuccess('MFA successfully reset. User will be required to set up MFA again on next login.')
    } catch (error) {
      this.setState(state => ({
        atom: state.atom.set('loading', false).set('error', Immutable.fromJS(error.response?.data || {}))
      }))
    }
  }

  handleResetMfaCancel = () => {
    this.setState({ showResetMfaDialog: false })
  }

  isUserLocked = () => {
    const lockedUntil = this.state.atom.getIn(['user', 'lockedUntil'])
    if (!lockedUntil) return false
    return new Date(lockedUntil) > new Date()
  }

  render() {
    const { atom } = this.state
    const user = atom.get('user')
    const email = user.get('email')?.trim() || ''
    const isEmailValid = email === '' || /^\S+@\S+\.\S+$/.test(email)
    const password = user.get('password') || ''
    const isPasswordValid =
      password.length >= 12 &&
      /[A-Z]/.test(password) &&
      /[a-z]/.test(password) &&
      /\d/.test(password) &&
      /[^A-Za-z0-9]/.test(password)
    const roles = atom.get('roles')
    const atomSelectedRole = atom.get('selectedRole')
    const { current } = this.props
    const { permissions } = current.user

    const isSupplierRecommended = atomSelectedRole && ['Store Manager', 'Store'].includes(atomSelectedRole.name)
    const hasSupplierWarning = isSupplierRecommended && !user.get('supplierId')

    let emailHelperText
    if (!email) {
      emailHelperText = 'Enables login with email and password resets'
    } else if (isEmailValid) {
      emailHelperText = 'Enables login with email and password resets'
    } else {
      emailHelperText = 'Not a valid email address'
    }

    const passwordRules = [
      { label: 'Be a minimum of 12 characters in length', valid: password.length >= 12 },
      { label: 'Contain at least 1 uppercase letter', valid: /[A-Z]/.test(password) },
      { label: 'Contain at least 1 lowercase letter', valid: /[a-z]/.test(password) },
      { label: 'Contain at least 1 number', valid: /\d/.test(password) },
      { label: 'Contain at least 1 special character', valid: /[^A-Za-z0-9]/.test(password) }
    ]

    let widgetTitle
    let submitButtonText
    if (this.props.editAccount) {
      widgetTitle = 'Edit Account'
      submitButtonText = 'Save'
    } else {
      widgetTitle = atom.get('mode') === 'new' ? 'Add Teammate' : 'Edit Teammate'
      submitButtonText = atom.get('mode') === 'new' ? 'Create teammate' : 'Update teammate'
    }

    return (
      <div className="container">
        <Widget
          breadcrumbs={
            !this.props.editAccount && [
              { to: '/admin', title: 'Admin' },
              { title: 'Teammates', to: '/admin/users' },
              { title: atom.get('mode') === 'new' ? 'New' : 'Edit' }
            ]
          }
          title={widgetTitle}
          loading={atom.get('loading')}
        >
          {atom.getIn(['error', 'message']) && (
            <div className="bp3-callout bp3-intent-danger" style={{ marginBottom: '20px' }}>
              <h5 className="bp3-callout-title bp3-heading">{atom.getIn(['error', 'message'])}</h5>
            </div>
          )}

          {atom.getIn(['error', 'errors', 'password']) && (
            <div className="bp3-callout bp3-intent-danger" style={{ marginBottom: '20px' }}>
              <h5 className="bp3-callout-title bp3-heading">Password Error</h5>
              <ul style={{ marginBottom: 0, paddingLeft: 20 }}>
                {atom.getIn(['error', 'errors', 'password']).map(error => (
                  <li key={error}>{error}</li>
                ))}
              </ul>
            </div>
          )}

          <form onSubmit={this.submit} style={{ maxWidth: 300 }}>
            {permissions.includes('settings.team') && <H4>Basic information</H4>}
            <FormGroup label="Full name">
              <InputGroup value={user.get('name')} onChange={e => this.handleChange('name', e.target.value)} />
            </FormGroup>

            <FormGroup label="Username">
              <InputGroup value={user.get('username')} onChange={e => this.handleChange('username', e.target.value)} />
            </FormGroup>

            <FormGroup
              label="Email"
              intent={email && !isEmailValid ? Intent.DANGER : Intent.NONE}
              helperText={emailHelperText}
            >
              <InputGroup autoComplete="off" value={email} onChange={e => this.handleChange('email', e.target.value)} />
            </FormGroup>

            <FormGroup
              label="Password"
              helperText={
                <div>
                  {password && !isPasswordValid && (
                    <div style={{ color: Colors.RED3, marginBottom: 4 }}>Password does not meet criteria</div>
                  )}

                  <strong>Passwords must:</strong>
                  <ul>
                    {passwordRules.map(rule => (
                      <li
                        key={rule.label}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          marginBottom: 5
                        }}
                      >
                        <span>{rule.label}</span>
                        <span style={{ color: rule.valid ? 'green' : 'red', fontWeight: 600 }}>
                          {rule.valid ? '✔' : '✖'}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              }
            >
              <InputGroup
                autoComplete="new-password"
                value={user.get('password')}
                type={this.state.showPassword ? 'text' : 'password'}
                onChange={e => this.handleChange('password', e.target.value)}
                rightElement={
                  user.get('password') ? (
                    <Button
                      minimal
                      small
                      onClick={() => this.setState(prev => ({ ...prev, showPassword: !prev.showPassword }))}
                    >
                      <strong>{this.state.showPassword ? 'HIDE' : 'SHOW'}</strong>
                    </Button>
                  ) : null
                }
              />
            </FormGroup>

            {permissions.includes('settings.team') && (
              <>
                <H4>Details</H4>
                <FormGroup
                  label="Role"
                  helperText={
                    this.props.current.user.id === this.state.atom.getIn(['user', 'id'])
                      ? 'You may lose access to this page if you change your own role'
                      : null
                  }
                >
                  {atomSelectedRole && (
                    <ControlGroup>
                      <HTMLSelect
                        fill
                        value={atomSelectedRole.id}
                        onChange={this.onRoleChangeEvent}
                        disabled={this.state.atom.get('roleInputDisabled')}
                      >
                        {roles.map(role => (
                          <option key={role.name} value={role.id}>
                            {role.name}
                          </option>
                        ))}
                      </HTMLSelect>
                      {this.state.atom.get('roleInputDisabled') && (
                        <AnchorButton
                          style={styles.userSelectNone}
                          onClick={() => {
                            this.setState(state => ({
                              atom: state.atom.set('roleInputDisabled', !state.atom.get('roleInputDisabled'))
                            }))
                          }}
                        >
                          Edit
                        </AnchorButton>
                      )}
                    </ControlGroup>
                  )}
                </FormGroup>

                <FormGroup
                  label="Supplier"
                  labelInfo={isSupplierRecommended ? 'optional, but encouraged for this role' : 'optional'}
                  intent={hasSupplierWarning ? Intent.DANGER : Intent.NONE}
                  helperText="Indicates a user that works for a supplier of your operation. They won't see manifests and orders from other suppliers"
                >
                  <LoadableSelect
                    load={Client.suppliersIndex}
                    style={hasSupplierWarning ? styles.supplierValidation : undefined}
                    process={d => d.suppliers.map(u => ({ value: u.id, label: u.name }))}
                    onChange={this.onAssignSupplier}
                    value={user.get('supplierId')}
                    placeholder="(None)"
                    noLabel
                    autoload={user.get('supplierId')}
                  />
                </FormGroup>

                {config.UPRIGHT_LOCAL() && user.get('supplierId') && (
                  <FormGroup
                    label="Upright Local store info footer"
                    labelInfo="optional"
                    helperText="A footer for products posted through Upright Local"
                  >
                    <textarea
                      style={{ width: '100%', minHeight: 150 }}
                      className="bp3-input"
                      placeholder="Address and Store Hours"
                      value={user.getIn(['localStoreAttributes', 'storeInfo']) || ''}
                      onChange={v => this.handleLocalStoreChange('storeInfo', v.target.value)}
                    />
                  </FormGroup>
                )}
                {current.organization.enabledMarkets.shopgoodwill &&
                  atomSelectedRole &&
                  atomSelectedRole.userPermissions.includes('products.post') && (
                    <>
                      <H4>Shopgoodwill login</H4>

                      <FormGroup label="Shopgoodwill username" labelInfo="optional">
                        <InputGroup
                          value={user.get('shopgoodwillUsername')}
                          onChange={e => this.handleChange('shopgoodwillUsername', e.target.value)}
                        />
                      </FormGroup>
                      <FormGroup label="Shopgoodwill password" labelInfo="optional">
                        <InputGroup
                          type="password"
                          value={user.get('shopgoodwillPassword')}
                          onChange={e => this.handleChange('shopgoodwillPassword', e.target.value)}
                        />
                      </FormGroup>
                    </>
                  )}
                {this.state.atom.get('mode') !== 'new' && (
                  <>
                    <H4>Activation status</H4>
                    <FormGroup
                      disabled={this.props.current.user.id === this.state.atom.getIn(['user', 'id'])}
                      label="Login enabled"
                      helperText="Whether or not this user is able to login and access Lister"
                    >
                      <HTMLSelect
                        fill
                        disabled={this.props.current.user.id === this.state.atom.getIn(['user', 'id'])}
                        onChange={v => {
                          const val = v.target.value === 'true'
                          this.setState(s => ({ atom: s.atom.setIn(['user', 'active'], val) }))
                        }}
                        value={this.state.atom.getIn(['user', 'active']) ? 'true' : 'false'}
                        options={[
                          // boolean values do not work for select values, so stringify them
                          { label: 'Active', value: 'true' },
                          { label: 'Inactive', value: 'false' }
                        ]}
                      />
                    </FormGroup>
                    {config.LOGIN_SECURITY() && (
                      <FormGroup label="Account lockout status">
                        {this.isUserLocked() ? (
                          <>
                            <div>
                              <Tag intent="warning" icon="lock" large round>
                                Locked
                              </Tag>
                              <div style={{ marginTop: 8, fontSize: 12, color: Colors.GRAY1 }}>
                                Locked until {new Date(user.get('lockedUntil')).toLocaleString()}
                                <br />
                                Reason: {user.get('lockoutReason') || 'Multiple failed login attempts'}
                                <br />
                                Failed attempts: {user.get('failedLoginAttempts') || 0}
                              </div>
                            </div>
                            {permissions.includes('settings.team') && (
                              <Button icon="unlock" style={{ marginTop: 8 }} onClick={this.handleUnlock}>
                                Unlock account
                              </Button>
                            )}
                          </>
                        ) : (
                          <Tag intent="success" icon="unlock" large round>
                            Unlocked
                          </Tag>
                        )}
                      </FormGroup>
                    )}
                    {permissions.includes('settings.team') && (
                      <FormGroup
                        label="Multi-factor authentication"
                        helperText="Resetting MFA will require the user to set up MFA again on their next login"
                      >
                        <Button intent="warning" icon="key" onClick={this.handleResetMfaClick}>
                          Reset MFA
                        </Button>
                        <Dialog
                          isOpen={this.state.showResetMfaDialog}
                          onClose={this.handleResetMfaCancel}
                          title="Reset Multi-Factor Authentication"
                          icon={IconNames.WARNING_SIGN}
                        >
                          <div className="bp3-dialog-body">
                            <h3>Are you sure you want to reset MFA for this user?</h3>
                            <p>
                              This will clear all Multi-Factor Authentication settings for{' '}
                              <strong>{user.get('name') || user.get('username')}</strong>. They will be required to set
                              up Multi-Factor Authentication again on their next login.
                            </p>
                            <p style={{ marginTop: 16, fontWeight: 600, color: Colors.ORANGE3 }}>
                              This action cannot be undone.
                            </p>
                          </div>
                          <div className="bp3-dialog-footer">
                            <div className="bp3-dialog-footer-actions">
                              <Button onClick={this.handleResetMfaCancel} minimal>
                                Cancel
                              </Button>
                              <Button intent={Intent.SUCCESS} onClick={this.handleResetMfaConfirm}>
                                Reset MFA
                              </Button>
                            </div>
                          </div>
                        </Dialog>
                      </FormGroup>
                    )}
                  </>
                )}
              </>
            )}

            <Button
              intent="primary"
              type="submit"
              disabled={
                !user.get('name')?.trim() ||
                !user.get('username')?.trim() ||
                !isEmailValid ||
                (password.length > 0 && !isPasswordValid)
              }
            >
              {submitButtonText}
            </Button>
          </form>
        </Widget>
      </div>
    )
  }
}

export default connect((state: AppState) => ({ current: state.current }))(NewUser)
